import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Material UI core and Icons
import {
  Box,
  Button,
  Card,
  CardActions,
  Container,
  IconButton,
  Link
} from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import RoomIcon from '@mui/icons-material/Room';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsIcon from '@mui/icons-material/Directions';
// component
import {
  AuthorizationError,
  reExportedColors,
  ITooltip,
  Text,
  Loader
} from '../../../components/1elements';
import DashboardWidgets from '../../../features/dashboard-v1/DashboardWidgets';
import { englishTexts as words } from '../../../consts/Texts';
import { isAllowedToViewAdminAccess } from '../../../services/permissions/permissionHelper';
import AssetImageCarouselCompact from './AssetImageCarouselCompact';

import { useQuickView } from '../../hooks/use-quick-view.hook';
import { useViewTab } from '../../hooks/use-view-tab.hook';

import CopyToClipboard from '../../../v2/components/CopyToClipboard';

import './styles.scss';

const QuickView = ({ mapSearchData, permissionGiven, loading, onClose }) => {
  const {
    address,
    address_coordinates,
    name,
    location_job_id,
    location_phone_number,
    transit_id
  } = mapSearchData;
  const { openGoogleMap } = useQuickView();
  const { tabs, handleClick } = useViewTab({ permissionGiven });

  const [firstAvailTab, setFirstAvailTab] = useState(undefined);
  const [mapOnMouseOver, setMapOnMouseOver] = useState(false);
  const [callOnMouseOver, setCallOnMouseOver] = useState(false);

  useEffect(() => {
    const availTab = tabs.find((tab) => !tab.isDisabled);
    setFirstAvailTab(availTab);
  }, [tabs]);

  if (loading) {
    return (
      <div style={{ marginTop: '10rem' }}>
        <Loader />
      </div>
    );
  }

  return (
    <Container disableGutters className="quick-view">
      {
        <Card className="quick-view-root">
          {permissionGiven || isAllowedToViewAdminAccess() ? (
            <React.Fragment>
              <IconButton
                aria-label="close quick view"
                className="quick-view-close"
                color="error"
                onClick={onClose}
              >
                <Close fontSize="small" />
              </IconButton>

              <AssetImageCarouselCompact
                assetId={transit_id}
                addressCoords={address_coordinates}
              />

              <Box className="quick-view-location-information" component="div">
                <Box
                  className="quick-view-wrapper"
                  component="section"
                  paddingBottom="0px"
                  marginBottom="0px"
                  borderBottom={`1px solid ${reExportedColors.HOVER}`}
                >
                  <Box component="div" className="quick-view-header">
                    <Box className="quick-view-building-detail" component="div">
                      <div className="copy-value-row">
                        {location_job_id && (
                          <>
                            <Text
                              type="locationViewText"
                              className="section-text"
                            >
                              Job ID:{' '}
                            </Text>
                            <CopyToClipboard value={location_job_id} />
                          </>
                        )}
                      </div>

                      <div className="copy-value-row">
                        <Text type="locationViewText" className="section-text">
                          {words.transitId}:{' '}
                        </Text>
                        <CopyToClipboard value={transit_id} />
                      </div>
                    </Box>

                    <Text
                      type="locationViewHeader"
                      className="quick-view-title"
                    >
                      {name}
                    </Text>
                  </Box>
                  <Box
                    component="div"
                    onClick={() => openGoogleMap(address_coordinates, address)}
                    onMouseEnter={() => setMapOnMouseOver(true)}
                    onMouseLeave={() => setMapOnMouseOver(false)}
                    className="quick-view-icon-details quick-view-link-details"
                  >
                    <CardActions>
                      <RoomIcon className="quick-view-icon" />
                      <span className="quick-view-link-text">{address}</span>
                    </CardActions>
                    {mapOnMouseOver ? (
                      <ITooltip placement="top" content="Direction">
                        <DirectionsIcon className="quick-view-on-hover-icon" />
                      </ITooltip>
                    ) : null}
                  </Box>
                  {location_phone_number && (
                    <Link
                      className="quick-view-link"
                      href={`tel:${location_phone_number}`}
                      underline="none"
                    >
                      <Box
                        className="quick-view-pt quick-view-icon-details quick-view-link-details"
                        component="div"
                        onMouseEnter={() => setCallOnMouseOver(true)}
                        onMouseLeave={() => setCallOnMouseOver(false)}
                      >
                        <CardActions>
                          <PhoneIcon className="quick-view-icon" />
                          <span className="quick-view-link-text">
                            {location_phone_number}
                          </span>
                        </CardActions>
                        {callOnMouseOver ? (
                          <ITooltip content="Make a Call" placement="top-end">
                            <PhoneIcon className="quick-view-on-hover-icon" />
                          </ITooltip>
                        ) : null}
                      </Box>
                    </Link>
                  )}
                </Box>
              </Box>
              <Box className="quick-view-scroll-div" component="div">
                <DashboardWidgets
                  locationId={mapSearchData.location_id || transit_id}
                  header
                  compact
                />
              </Box>

              <Box component="div" className="quick-view-action-wrapper">
                <Button
                  variant="outlined"
                  endIcon={<ChevronRight />}
                  fullWidth={true}
                  className="quick-view-action"
                  disabled={!firstAvailTab}
                  onClick={() => {
                    handleClick(
                      firstAvailTab.sys_name,
                      window.localStorage.getItem('locId')
                    );
                  }}
                >
                  View Details
                </Button>
              </Box>
            </React.Fragment>
          ) : (
            <div className="quick-view-no-access-card quick-view-root">
              <AuthorizationError />
            </div>
          )}
        </Card>
      }
    </Container>
  );
};

QuickView.propTypes = {
  mapSearchData: PropTypes.any,
  permissionGiven: PropTypes.any,
  loading: PropTypes.bool,
  onClose: PropTypes.func
};

export default memo(QuickView);
